import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { DetailPage, DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { CustomersList } from "@/components/inline-entity-list";
import { CreateCustomerDialog } from "@/components/dialogs";
import { ImpersonateButton } from "@/components/impersonate-button";
import { useOrgSettings } from "@/lib/org-settings-context";

interface BusinessUnit {
  id: string;
  name: string;
  slug: string;
  organizationId: string;
  email: string | null;
  phone: string | null;
  status: string;
  cardProgramName: string | null;
  maxCardsPerCustomer: number;
  defaultDailyLimitCents: number | null;
  defaultMonthlyLimitCents: number | null;
  createdAt: string;
  updatedAt: string;
}

export default function BusinessUnitDetail() {
  const { toast } = useToast();
  const { formatCurrency } = useOrgSettings();
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<BusinessUnit>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);

  const { data: bu, isLoading } = useQuery<BusinessUnit>({
    queryKey: ["business-unit", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/business-units/${params.id}`);
      if (!response.ok) throw new Error("Failed to fetch business unit");
      return response.json();
    },
    enabled: !!params.id,
  });

  // Fetch customer count for tab label
  const { data: customers } = useQuery<{ id: string }[]>({
    queryKey: ["bu-customers", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/customers?businessUnitId=${params.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!params.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<BusinessUnit>) => {
      const response = await apiRequest("PATCH", `/api/business-units/${params.id}`, updates);
      if (!response.ok) throw new Error("Failed to update business unit");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-unit", params.id] });
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Business unit updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update business unit", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/business-units/${params.id}`);
      if (!response.ok) throw new Error("Failed to delete business unit");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      toast({ title: "Success", description: "Business unit deleted successfully" });
      setLocation("/business-unit/view");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete business unit", variant: "destructive" });
    },
  });


  const handleEdit = () => {
    if (bu) {
      setEditForm({
        name: bu.name,
        email: bu.email || "",
        phone: bu.phone || "",
        cardProgramName: bu.cardProgramName || "",
        maxCardsPerCustomer: bu.maxCardsPerCustomer,
        status: bu.status,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editForm);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      ACTIVE: "default",
      INACTIVE: "secondary",
      PENDING: "outline",
      SUSPENDED: "destructive",
    };
    const colors: Record<string, string> = {
      ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    };
    return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
  };


  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!bu) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Business unit not found</p>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Business Units", href: "/business-unit/view" },
    { label: bu.name },
  ];

  const tabs = [
    {
      id: "overview",
      label: "Overview",
      content: (
        <div className="space-y-6">
          {/* Basic Information */}
          <DetailCard title="Basic Information">
            <DetailGrid columns={2}>
              <DetailItem label="Company Name" value={bu.name} />
              <DetailItem label="Slug" value={bu.slug} />
              <DetailItem label="Card Program" value={bu.cardProgramName || "-"} />
              <DetailItem label="Email" value={bu.email || "-"} />
              <DetailItem label="Phone" value={bu.phone || "-"} />
            </DetailGrid>
          </DetailCard>

          {/* Card Limits */}
          <DetailCard title="Card Configuration">
            <DetailGrid columns={3}>
              <DetailItem label="Max Cards Per Customer" value={bu.maxCardsPerCustomer.toString()} />
              <DetailItem label="Default Daily Limit" value={formatCurrency(bu.defaultDailyLimitCents)} />
              <DetailItem label="Default Monthly Limit" value={formatCurrency(bu.defaultMonthlyLimitCents)} />
            </DetailGrid>
          </DetailCard>

          {/* Status */}
          <DetailCard title="Status">
            <DetailGrid columns={2}>
              <DetailItem label="Status" value={getStatusBadge(bu.status)} />
            </DetailGrid>
          </DetailCard>

          {/* Record Information */}
          <DetailCard title="Record Information">
            <DetailGrid columns={2}>
              <DetailItem label="ID" value={<code className="text-xs bg-muted px-2 py-1 rounded">{bu.id}</code>} />
              <DetailItem label="Organization ID" value={<code className="text-xs bg-muted px-2 py-1 rounded">{bu.organizationId}</code>} />
              <DetailItem label="Created" value={new Date(bu.createdAt).toLocaleString()} />
              <DetailItem label="Last Updated" value={new Date(bu.updatedAt).toLocaleString()} />
            </DetailGrid>
          </DetailCard>
        </div>
      ),
    },
    {
      id: "customers",
      label: `Customers (${customers?.length || 0})`,
      content: (
        <CustomersList
          businessUnitId={bu.id}
          onCreate={() => setCreateCustomerOpen(true)}
        />
      ),
    },
    {
      id: "activity",
      label: "Activity",
      content: (
        <ActivityTab
          entityType="business_unit"
          entityId={bu.id}
          createdAt={bu.createdAt}
          updatedAt={bu.updatedAt}
        />
      ),
    },
  ];

  // Edit mode content
  const editContent = (
    <DetailCard title="Edit Business Unit">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Company Name</Label>
          <Input
            id="name"
            value={editForm.name || ""}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cardProgramName">Card Program Name</Label>
          <Input
            id="cardProgramName"
            value={editForm.cardProgramName || ""}
            onChange={(e) => setEditForm({ ...editForm, cardProgramName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={editForm.email || ""}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={editForm.phone || ""}
            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="maxCards">Max Cards Per Customer</Label>
          <Input
            id="maxCards"
            type="number"
            value={editForm.maxCardsPerCustomer || 1}
            onChange={(e) => setEditForm({ ...editForm, maxCardsPerCustomer: parseInt(e.target.value) })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={editForm.status}
            onValueChange={(value) => setEditForm({ ...editForm, status: value })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
              <SelectItem value="SUSPENDED">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </DetailCard>
  );

  return (
    <>
      <DetailPage
        breadcrumbs={breadcrumbs}
        title={bu.name}
        subtitle={bu.cardProgramName || ca.slug}
        badge={getStatusBadge(bu.status)}
        headerActions={
          <ImpersonateButton
            entityType="BUSINESS_UNIT"
            entityId={bu.id}
            entityName={bu.name}
            variant="outline"
          />
        }
        tabs={isEditing ? undefined : tabs}
        onEdit={!isEditing ? handleEdit : undefined}
        onDelete={!isEditing ? () => setDeleteDialogOpen(true) : undefined}
        isDeleting={deleteMutation.isPending}
        deleteDialogOpen={deleteDialogOpen}
        onDeleteDialogChange={setDeleteDialogOpen}
        deleteDialogTitle="Delete Business Unit"
        deleteDialogDescription={`Are you sure you want to delete "${bu.name}"? This action cannot be undone and will also delete all associated customers, cards, and transactions.`}
        onDeleteConfirm={() => deleteMutation.mutate()}
        isEditing={isEditing}
      >
        {isEditing && editContent}
      </DetailPage>

      <CreateCustomerDialog
        open={createCustomerOpen}
        onOpenChange={setCreateCustomerOpen}
        businessUnitId={bu.id}
      />
    </>
  );
}
